import { AnimatedSprite, Sprite, Texture } from 'pixi.js';
import { FSM, Store, TextureFactory } from '../../core';
import { IEventEmitter, Point, StatefulAnimation, StateMap, Vector } from '../../interface';
import { BaseObject } from '../';
import PlayerTextureInfo from './Player.json';
import JumpingPlayerTextureInfo from '../jumping/Player.json';
import { PowerProgressBar } from '../../ui';
import { sound } from '@pixi/sound';

interface IPlayer extends IEventEmitter {
  hp: number;
  score: number;
  velocity: Vector;
  position: Point;
  dashDT: number;
}

class Player extends BaseObject {
  public hp: number;
  public score: number;

  private fsm: FSM<IPlayer>;
  private statefulAnimation: StatefulAnimation;
  public animatedSprite: AnimatedSprite;
  private arrowSprite: Sprite;
  public speed: number;
  public radian: number;
  public targetRadian: number;
  public invincibilityDT: number;
  public dashDT: number;
  public targetPosition: Point;
  private progress: PowerProgressBar;

  constructor() {
    super();
    this.hp = Store.instance.state.hp;
    this.score = Store.instance.state.score;
    this.invincibilityDT = 0;
    this.dashDT = 0;
    this.targetPosition = { x: 0, y: 0 };

    const textures = TextureFactory.instance.load(PlayerTextureInfo);
    const clearTextures = TextureFactory.instance.load(JumpingPlayerTextureInfo);

    this.statefulAnimation = {
      IDLE: {
        textures: textures.slice(0, 12),
        animationSpeed: 0.2,
        offset: { x: 0, y: 0 },
        loop: true,
      },
      WALK: {
        textures: textures.slice(0, 12),
        animationSpeed: 0.2,
        offset: { x: 0, y: 0 },
        loop: true,
      },
      BOOST: {
        textures: textures.slice(13, 22),
        animationSpeed: 0.4,
        offset: { x: 0, y: 0 },
        loop: true,
      },
      HIT: {
        textures: textures.slice(23, 36),
        animationSpeed: 0.15,
        offset: { x: 0, y: 0 },
        loop: false,
      },
      DIE: {
        textures: textures.slice(37, 136),
        animationSpeed: 0.6,
        offset: { x: 0, y: 0 },
        loop: false,
      },
      CLEAR: {
        textures: clearTextures.slice(127, 157),
        animationSpeed: 0.4,
        offset: { x: 0, y: 0 },
        loop: false,
      },
    };

    this.speed = 5;
    this.radian = 0;
    this.targetRadian = 0;

    const texture = TextureFactory.instance.getSingleTexture('/textures/etc/arrow.png');
    this.arrowSprite = new Sprite(texture);
    this.arrowSprite.scale.set(0.6);
    this.arrowSprite.x = 150;
    this.stage.addChild(this.arrowSprite);

    this.animatedSprite = new AnimatedSprite([Texture.EMPTY]);
    this.animatedSprite.updateAnchor = true;
    this.animatedSprite.onComplete = () => this.fsm.trigger('ANIMATION_DONE');
    this.animatedSprite.y += 35;
    this.animatedSprite.scale.set(1.25);
    this.fsm = new FSM<IPlayer>(this, this.states, 'IDLE');
    this.stage.addChild(this.animatedSprite);

    this.progress = new PowerProgressBar(0, 100);
    this.progress.alpha = 0;
    this.progress.y = -200;
    this.progress.scale.set(2);
    this.ui.addChild(this.progress);
  }

  public get state(): string {
    return this.fsm.state || '';
  }

  public trigger(...events: Array<string>): void {
    this.fsm.trigger(...events);
  }

  public update(elapsed: number): void {
    super.update(elapsed);

    if (this.invincibilityDT > 0) {
      this.animatedSprite.alpha = Math.floor(this.invincibilityDT / 5) % 2 === 0 ? 1 : 0.5;
      this.invincibilityDT -= elapsed;
    } else {
      this.invincibilityDT = 0;
      this.animatedSprite.alpha = 1;
    }

    if (this.dashDT > elapsed) {
      this.dashDT -= elapsed;
      this.speed = 40;

      if (this.progress.alpha < 1 - elapsed * 0.05) {
        this.progress.alpha += elapsed * 0.05;
      } else {
        this.progress.alpha = 1;
      }
    } else {
      this.dashDT = 0;

      if (this.progress.alpha > elapsed * 0.05) {
        this.progress.alpha -= elapsed * 0.05;
      } else {
        this.progress.alpha = 0;
      }
    }

    this.progress.update(this.dashDT, 300);

    this.velocity.x = Math.cos(this.radian) * this.speed;
    this.velocity.y = Math.sin(this.radian) * this.speed;

    if (this.state === 'WALK' || this.state === 'BOOST') {
      this.animatedSprite.animationSpeed = 0.25 + this.speed / 80;
    }

    this.fsm.update();

    this.radian += (this.targetRadian - this.radian) * 0.1 * elapsed;
    this.arrowSprite.rotation = this.radian;
    this.arrowSprite.x = Math.cos(this.radian) * 150;
    this.arrowSprite.y = Math.sin(this.radian) * 150;
    this.animatedSprite.rotation = this.radian / 2;
  }

  public changeDirection(): void {
    if (this.targetRadian === 0) {
      this.emit('firstDirectionChanged');
    }

    if (this.radian > 0) {
      this.targetRadian = (-Math.PI / 180) * 35;
    } else {
      this.targetRadian = (Math.PI / 180) * 35;
    }
  }

  public dash(duration: number): void {
    this.dashDT = duration;
    this.invincibilityDT = duration + 180;
  }

  public hit(damage: number): void {
    if (this.invincibilityDT === 0) {
      sound.play('/audio/crash.mp3', { loop: false, volume: Store.instance.state.volume as number });
      sound.play('/audio/heartlose.mp3', { loop: false, volume: Store.instance.state.volume as number });
      this.hp -= damage;
      this.speed = 5;

      if (this.hp > 0) {
        this.fsm.trigger('HIT');
      } else {
        this.hp = 0;
        this.speed = 0;
        this.velocity.x = 0;
        this.velocity.y = 0;
        this.fsm.trigger('DIE');
        this.emit('DIE');
      }
      this.vibrator.vibrate(12);
      this.invincibilityDT = 180;
      this.emit('hit', damage);
    }
  }

  private get states(): StateMap<IPlayer> {
    return {
      IDLE: {
        CLEAR: [{ events: ['CLEAR'] }],
        DIE: [{ events: ['DIE'] }],
        HIT: [{ events: ['HIT'] }],
        BOOST: [{ refs: ['dashDT'] }, '>', 0],
        WALK: [[['abs', { refs: ['velocity', 'x'] }], '>=', 0], '||', [['abs', { refs: ['velocity', 'y'] }], '>=', 0]],
      },
      WALK: {
        CLEAR: [{ events: ['CLEAR'] }],
        DIE: [{ events: ['DIE'] }],
        HIT: [{ events: ['HIT'] }],
        BOOST: [{ refs: ['dashDT'] }, '>', 0],
        IDLE: [[{ refs: ['velocity', 'x'] }, '===', 0], '&&', [{ refs: ['velocity', 'y'] }, '===', 0]],
      },
      BOOST: {
        CLEAR: [{ events: ['CLEAR'] }],
        DIE: [{ events: ['DIE'] }],
        HIT: [{ events: ['HIT'] }],
        IDLE: [{ refs: ['dashDT'] }, '<=', 0],
      },
      HIT: {
        CLEAR: [{ events: ['CLEAR'] }],
        DIE: [{ events: ['DIE'] }],
        HIT: [{ events: ['HIT'] }],
        BOOST: [{ refs: ['dashDT'] }, '>', 0],
        IDLE: [{ events: ['ANIMATION_DONE'] }],
      },
      DIE: {},
      CLEAR: {},
    };
  }

  public transition(prevState: string, nextState: string): void {
    const info = this.statefulAnimation[nextState];
    const flipX = this.animatedSprite.scale.x < 0 ? -1 : 1;

    this.animatedSprite.textures = info.textures;
    this.animatedSprite.scale.set(flipX * Math.abs(this.animatedSprite.scale.x), this.animatedSprite.scale.y);
    this.animatedSprite.animationSpeed = info.animationSpeed;
    this.animatedSprite.loop = info.loop;

    this.animatedSprite.gotoAndPlay(0);
  }
}

export default Player;
